import React, { useState, useEffect } from 'react'
import ChartContainer from '../../components/analytics/ChartContainer'
import { parse, format } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import CustomTooltip from '../../components/analytics/CustomTooltip';

const COLORS_PALETTE = ['#ffae00', '#f56a20', '#f53155', '#f25ebf', '#28bdfc', '#58bfba', '#02c25b'];

const STEP_LABELS = {
    LOGIN: 'User logged in',
    PRIMARY_DOCUMENT: 'Completed primary document capture',
    SECONDARY_DOCUMENT: 'Completed secondary document capture',
    SELFIE_LIVENESS: 'Completed selfie capture',
    APPLICATION_SUMMARY: 'Application summary viewed',
    APPLICATION_SUBMIT: 'Submitted to Review Center',
};

function CustomLegend({ payload }) {
    return (
        <div style={{}}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', columnGap: '0.5rem', rowGap: '0.125rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '1rem' }}>
                {payload.map((entry, index) => (
                    <li key={`item-${index}`} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                        <span
                            style={{
                                display: 'inline-block',
                                width: 12,
                                height: 12,
                                backgroundColor: entry.color,
                                marginRight: 8,
                                borderRadius: 2,
                            }}
                        />
                        <span style={{ fontSize: 12 }}>
                            {STEP_LABELS[entry.value] || entry.value}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}


const DropOff = ({
    isLoading,
    errorMessage,
    isValueZero,
    data,
    chartDescription
}) => {
    const [chartData, setChartData] = useState([]);
    const [uniqueSteps, setUniqueSteps] = useState([]);

    useEffect(() => {
        const formatted = formatDataForChart(data);
        setChartData(formatted);
        console.log("Drop Off Chart Data: ", formatted);

        const stepOrder = [
            'LOGIN',
            'PRIMARY_DOCUMENT',
            'SECONDARY_DOCUMENT',
            'SELFIE_LIVENESS',
            'APPLICATION_SUMMARY',
            'APPLICATION_SUBMIT'
        ];

        const steps = Object.keys(formatted[0] || {})
            .filter(key => key !== 'month')
            .sort((a, b) => stepOrder.indexOf(a) - stepOrder.indexOf(b));

        setUniqueSteps(steps);
        console.log("Unique Steps: ", steps);
    }, [data]);

    return (
        <ChartContainer
            title="Completion rate on IDV (Month-on-Month)"
            chartDescription={chartDescription}
            isLoading={isLoading}
            errorMessage={errorMessage}
            isValueZero={isValueZero}
            style={{ position: 'relative' }}
        >
            <BarChart
                width={460}
                height={400}
                data={chartData}
                margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="month"
                    interval={0}
                    tick={{ fontSize: 12 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip
                    content={
                        <CustomTooltip
                            labelFormatter={(label) => `Month: ${label}`}
                            valueFormatter={(value) => `${value}`}
                        />
                    }
                />
                <Legend
                    content={
                        <CustomLegend
                            labelMapping={STEP_LABELS}
                        />
                    }
                />
                {uniqueSteps.map((completedStep, index) => (
                    <Bar
                        key={completedStep}
                        dataKey={completedStep}
                        name={STEP_LABELS[completedStep] || completedStep}
                        fill={COLORS_PALETTE[index % COLORS_PALETTE.length]} />
                ))}
            </BarChart>
        </ChartContainer>
    )
}

function formatDataForChart(rawData) {
    const monthStepMap = new Map();
    const allSteps = new Set();

    rawData.forEach(item => {
        const date = parse(item.completedAt, 'yyyy-MM-dd hh:mm a', new Date());
        const month = format(date, "MMM''yy");

        allSteps.add(item.completedStep);

        if (!monthStepMap.has(month)) {
            monthStepMap.set(month, {});
        }

        const stepCounts = monthStepMap.get(month);
        stepCounts[item.completedStep] = (stepCounts[item.completedStep] || 0) + 1;
    });

    const sortedMonths = Array.from(monthStepMap.keys()).sort(
        (a, b) => new Date(`01 ${a}`) - new Date(`01 ${b}`)
    );

    return sortedMonths.map(month => {
        const stepCounts = monthStepMap.get(month);
        const row = { month };
        allSteps.forEach(step => {
            row[step] = stepCounts[step] || 0;
        });
        return row;
    });
}

export default DropOff;